import React, { useEffect, useMemo, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import { brand } from "../../../theme/businessFlowsTheme";
import { resetViewSetting, saveViewSetting } from "../../../actions";
import ExplorerStartCard from "./ExplorerStartCard";
import { ALWAYS_KEPT_ATTRIBUTES } from "./names";

const GROUPS = [
  { prefix: "search.", title: "Advanced search" },
  { prefix: "quickSearch.", title: "Quick search" },
  { prefix: "explorer.", title: "Relationship explorer" },
  { prefix: "card.", title: "Search result cards" },
  { prefix: "hover.", title: "Explorer fact sheet" },
  { prefix: "diagram.", title: "Diagrams and maps" },
  // Where an object occurs: the maps that draw it, and how far into the
  // stored diagrams the search for it may go.
  { prefix: "occurrence.", title: "Occurrences" },
  { prefix: "editor.", title: "Content editor" },
  { prefix: "", title: "Attributes" },
];
const groupOf = (key) => GROUPS.find((group) => group.prefix && key.startsWith(group.prefix)) || GROUPS[GROUPS.length - 1];

const describe = (definition, value) => {
  if (definition.kind === "boolean") return value ? "on" : "off";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "nothing";
  if (value === null || value === undefined || value === "") return definition.kind === "type" ? "every type" : "nothing";
  return String(value);
};

// Inputs fill the row on a phone and keep their width from sm up.
const widthFrom = (width) => ({ width: { xs: "100%", sm: width } });

// The hidden attributes the server keeps whatever the list says.
const lockedOf = (definition) =>
  definition.key === "ignoredAttributes" ? (Array.isArray(definition.alwaysKeeps) ? definition.alwaysKeeps : ALWAYS_KEPT_ATTRIBUTES) : [];

function SettingField({ definition, types, mayManage, onSave, onReset }) {
  const [draft, setDraft] = useState(definition.value);
  useEffect(() => setDraft(definition.value), [definition.value]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(definition.value);
  const valid =
    definition.kind !== "number" || (Number.isInteger(draft) && draft >= definition.min && draft <= definition.max);
  // A switch has nothing to confirm: it saves the moment it is flipped, the
  // way every other switch in the console does.
  const saveNow = (value) => {
    setDraft(value);
    onSave(definition, value);
  };
  const labelOf = (name) => types.find((t) => t.objectType === name)?.label || name;
  const locked = lockedOf(definition);
  const isLocked = (value) => locked.includes(value);

  let input;
  if (!mayManage) {
    const typeNames = (value) => (Array.isArray(value) ? value.map(labelOf) : value && labelOf(value));
    const shown = describe(definition, definition.kind === "type" || definition.kind === "typeList" ? typeNames(definition.value) : definition.value);
    input = (
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0, fontWeight: 600, overflowWrap: "anywhere" }}>
        {shown}
      </Typography>
    );
  } else {
    switch (definition.kind) {
      case "boolean":
        input = (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Switch
              checked={draft === true}
              onChange={(e) => saveNow(e.target.checked)}
              inputProps={{ "aria-label": definition.label }}
            />
          </Box>
        );
        break;
      case "number":
        input = (
          <TextField
            size="small"
            type="number"
            fullWidth
            value={draft ?? ""}
            error={!valid}
            helperText={valid ? " " : `${definition.min} to ${definition.max}`}
            onChange={(e) => setDraft(e.target.value === "" ? "" : Number(e.target.value))}
            inputProps={{ min: definition.min, max: definition.max, "aria-label": definition.label }}
            sx={widthFrom(140)}
          />
        );
        break;
      case "text":
        input = (
          <TextField
            size="small"
            fullWidth
            value={draft ?? ""}
            onChange={(e) => setDraft(e.target.value)}
            inputProps={{ maxLength: 40, "aria-label": definition.label }}
            sx={widthFrom(240)}
          />
        );
        break;
      case "type":
        input = (
          <TextField
            select
            size="small"
            fullWidth
            value={draft || ""}
            onChange={(e) => setDraft(e.target.value || null)}
            SelectProps={{ displayEmpty: true }}
            inputProps={{ "aria-label": definition.label }}
            sx={widthFrom(260)}
          >
            <MenuItem value="">Every type</MenuItem>
            {types.map((t) => (
              <MenuItem key={t.objectType} value={t.objectType}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>
        );
        break;
      case "typeList":
        input = (
          <Autocomplete
            multiple
            size="small"
            options={types.map((t) => t.objectType)}
            getOptionLabel={labelOf}
            value={draft || []}
            onChange={(e, value) => setDraft(value)}
            renderInput={(params) => <TextField {...params} placeholder="Add a type" inputProps={{ ...params.inputProps, "aria-label": definition.label }} />}
            sx={{ flex: 1, width: { xs: "100%", md: "auto" }, minWidth: { xs: 0, sm: 240 } }}
          />
        );
        break;
      default:
        input = (
          <Autocomplete
            multiple
            freeSolo
            size="small"
            options={[]}
            value={draft || []}
            // What the server always keeps stays in the list, whatever is removed.
            onChange={(e, value) => {
              const next = value.map((v) => String(v).trim()).filter(Boolean);
              setDraft([...locked.filter((key) => (draft || []).includes(key) && !next.includes(key)), ...next]);
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, onDelete, ...tagProps } = getTagProps({ index });
                return isLocked(option) ? (
                  <Chip
                    key={option}
                    {...tagProps}
                    size="small"
                    icon={<LockRoundedIcon />}
                    label={option}
                    title="Always hidden"
                    aria-label={`${option}, always hidden`}
                  />
                ) : (
                  <Chip key={option} {...tagProps} onDelete={onDelete} size="small" label={option} />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Type and press Enter"
                helperText={locked.length ? `${locked.join(", ")} are always hidden: the id and the diagram contents.` : undefined}
                inputProps={{ ...params.inputProps, "aria-label": definition.label }}
              />
            )}
            sx={{ flex: 1, width: { xs: "100%", md: "auto" }, minWidth: { xs: 0, sm: 240 } }}
          />
        );
    }
  }

  return (
    <Box sx={{ py: 1.5, borderTop: `1px solid ${brand.line}` }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
        <Box sx={{ flex: { md: "0 0 280px" }, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ fontWeight: 650 }}>
              {definition.label}
            </Typography>
            {definition.configured && <Chip size="small" label="Changed" sx={{ height: 20, fontSize: "0.7rem", bgcolor: "rgba(240,230,49,0.35)" }} />}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {definition.help} Default: {describe(definition, definition.default)}.
          </Typography>
        </Box>
        {input}
        {mayManage && (
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
            {definition.kind !== "boolean" && (
              <Button size="small" variant="contained" disabled={!dirty || !valid} onClick={() => onSave(definition, draft)}>
                Save
              </Button>
            )}
            {definition.configured && (
              <Button size="small" startIcon={<RestartAltRoundedIcon />} onClick={() => onReset(definition)} aria-label={`Back to the default for ${definition.label}`}>
                Back to the default
              </Button>
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

// Every other display decision a database carries: page sizes, sort fields,
// the starting type, lengths, format names, how diagrams and maps are drawn,
// hidden attributes, editor texts, and where the explorer starts. The tab
// draws whatever the server defines, so a new setting needs nothing here.
export default function SettingsTab({ overview, database, mayManage, apply, askReset }) {
  const types = overview.types || [];
  const groups = useMemo(() => {
    const definitions = overview.settingDefinitions || [];
    return GROUPS.map((group) => ({ ...group, items: definitions.filter((definition) => groupOf(definition.key) === group) })).filter(
      (group) => group.items.length > 0 || group.prefix === "explorer."
    );
  }, [overview.settingDefinitions]);

  const save = (definition, value) => apply(() => saveViewSetting(database, definition.key, value), `${definition.label} saved.`);
  const reset = (definition) =>
    askReset({
      title: `Back to the default for “${definition.label}”?`,
      text: `It goes back to ${describe(definition, definition.default)}.`,
      request: () => resetViewSetting(database, definition.key),
      message: `${definition.label} is back to its default.`,
    });

  return (
    <Stack spacing={2}>
      {groups.map((group) => (
        <Paper key={group.title} elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 4, border: `1px solid ${brand.line}` }}>
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>{group.title}</Typography>
          {group.prefix === "explorer." && <ExplorerStartCard database={database} types={types} mayManage={mayManage} apply={apply} askReset={askReset} />}
          {group.items.map((definition) => (
            <SettingField key={definition.key} definition={definition} types={types} mayManage={mayManage} onSave={save} onReset={reset} />
          ))}
        </Paper>
      ))}
    </Stack>
  );
}
